import './instrument';
import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { App } from './app';
import { PUBLIC_ROUTES } from './routes';
import './styles/index.css';

const root = document.getElementById('root');

if (!root) {
  throw new Error('Root element not found');
}

const app = (
  <StrictMode>
    <App />
  </StrictMode>
);

// Public routes are prerendered at build time; the prerenderer stamps the
// route it rendered into __PRERENDER_PATH__. Hydrate only when the served
// document actually matches the requested path - the SPA fallback rewrite
// can serve a prerendered page for a different route (e.g. /dashboard).
declare global {
  interface Window {
    __PRERENDER_PATH__?: string;
  }
}

const normalise = (path: string) =>
  path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;

const prerenderedPath = window.__PRERENDER_PATH__;
const isMatchingPrerender =
  prerenderedPath !== undefined &&
  root.hasChildNodes() &&
  normalise(window.location.pathname) === prerenderedPath;

function removeManagedHeadTags() {
  document.head
    .querySelectorAll(
      'title, meta[name="description"], meta[name="robots"], link[rel="canonical"], ' +
        'meta[property^="og:"], meta[name^="twitter:"], meta[property^="article:"]'
    )
    .forEach((el) => el.remove());
}

if (isMatchingPrerender) {
  hydrateRoot(root, app);
} else {
  const currentPath = normalise(window.location.pathname);
  const shouldClearManagedHead = root.hasChildNodes() || PUBLIC_ROUTES.includes(currentPath);

  if (root.hasChildNodes()) {
    // Fallback-served prerendered document for a different route: discard the
    // stale markup and the head tags the prerenderer baked in, then render
    // fresh. (None of the non-prerendered routes manage their own metadata,
    // so restore the default title.)
    root.innerHTML = '';
  }
  if (shouldClearManagedHead) {
    removeManagedHeadTags();
    document.title = 'Fixtyer | Prepare a UK Pothole Damage Claim Pack';
  }
  createRoot(root).render(app);
}
